import React, { useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HomeIcon, BarChartIcon, LineChartIcon, PieChartIcon, TrendingUpIcon, SettingsIcon, ShieldCheckIcon, BuildingIcon, UsersIcon, UploadIcon, XIcon } from 'lucide-react';
interface SidebarProps {
  isMobileMenuOpen: boolean;
  onCloseMobileMenu: () => void;
}
const Sidebar: React.FC<SidebarProps> = ({
  isMobileMenuOpen,
  onCloseMobileMenu
}) => {
  const sidebarRef = useRef<HTMLDivElement>(null);
  // Simular que el usuario es Super Admin
  const isSuperAdmin = true;
  // Close mobile menu when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobileMenuOpen && sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        onCloseMobileMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen, onCloseMobileMenu]);
  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);
  return <>
      {/* Backdrop overlay for mobile */}
      <AnimatePresence data-id="element-173">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden" onClick={onCloseMobileMenu} data-id="element-174" />}
      </AnimatePresence>
      {/* Sidebar - Desktop version (always visible) */}
      <aside className="hidden md:flex md:relative w-64 bg-white border-r border-gray-200 flex-col h-full" data-id="element-175">
        <div className="p-4 border-b border-gray-200" data-id="element-176">
          <h1 className="text-xl font-bold text-blue-600" data-id="element-177">FinancePro</h1>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-id="element-178">
          {isSuperAdmin && <>
              <div className="px-3 py-2" data-id="element-179">
                <div className="flex items-center space-x-2 text-xs font-semibold text-gray-500 uppercase" data-id="element-180">
                  <ShieldCheckIcon size={14} data-id="element-181" />
                  <span data-id="element-182">Administración</span>
                </div>
              </div>
              <NavItem to="/admin/home" icon={<ShieldCheckIcon size={20} data-id="element-184" />} label="Panel Admin" data-id="element-183" />
              <NavItem to="/admin/companies" icon={<BuildingIcon size={20} data-id="element-186" />} label="Empresas" data-id="element-185" />
              <NavItem to="/admin/users" icon={<UsersIcon size={20} data-id="element-188" />} label="Usuarios" data-id="element-187" />
              <NavItem to="/admin/data-import" icon={<UploadIcon size={20} data-id="element-190" />} label="Importar datos" data-id="element-189" />
              <div className="border-t border-gray-200 my-2" data-id="element-191"></div>
              <div className="px-3 py-2" data-id="element-192">
                <div className="flex items-center space-x-2 text-xs font-semibold text-gray-500 uppercase" data-id="element-193">
                  <HomeIcon size={14} data-id="element-194" />
                  <span data-id="element-195">Vista Empresas</span>
                </div>
              </div>
            </>}
          <NavItem to="/" icon={<HomeIcon size={20} data-id="element-197" />} label="Dashboard" data-id="element-196" />
          <NavItem to="/income-statement" icon={<BarChartIcon size={20} data-id="element-199" />} label="Estado de Resultados" data-id="element-198" />
          <NavItem to="/cash-flow" icon={<LineChartIcon size={20} data-id="element-201" />} label="Flujo de Efectivo" data-id="element-200" />
          <NavItem to="/balance-sheet" icon={<PieChartIcon size={20} data-id="element-203" />} label="Balance General" data-id="element-202" />
          <NavItem to="/trends" icon={<TrendingUpIcon size={20} data-id="element-205" />} label="Tendencias" data-id="element-204" />
          <NavItem to="/settings" icon={<SettingsIcon size={20} data-id="element-207" />} label="Configuración" data-id="element-206" />
        </nav>
      </aside>
      {/* Sidebar - Mobile version (animated) */}
      <motion.aside ref={sidebarRef} initial={false} animate={{
      x: isMobileMenuOpen ? 0 : '-100%'
    }} transition={{
      type: 'spring',
      stiffness: 300,
      damping: 30
    }} className="md:hidden fixed w-64 bg-white border-r border-gray-200 flex flex-col h-full z-50" data-id="element-208">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between" data-id="element-209">
          <h1 className="text-xl font-bold text-blue-600" data-id="element-210">FinancePro</h1>
          <button onClick={onCloseMobileMenu} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" aria-label="Close menu" data-id="element-211">
            <XIcon size={20} className="text-gray-600" data-id="element-212" />
          </button>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-id="element-213">
          {isSuperAdmin && <>
              <div className="px-3 py-2" data-id="element-214">
                <div className="flex items-center space-x-2 text-xs font-semibold text-gray-500 uppercase" data-id="element-215">
                  <ShieldCheckIcon size={14} data-id="element-216" />
                  <span data-id="element-217">Administración</span>
                </div>
              </div>
              <NavItem to="/admin/home" icon={<ShieldCheckIcon size={20} data-id="element-219" />} label="Panel Admin" onClick={onCloseMobileMenu} data-id="element-218" />
              <NavItem to="/admin/companies" icon={<BuildingIcon size={20} data-id="element-221" />} label="Empresas" onClick={onCloseMobileMenu} data-id="element-220" />
              <NavItem to="/admin/users" icon={<UsersIcon size={20} data-id="element-223" />} label="Usuarios" onClick={onCloseMobileMenu} data-id="element-222" />
              <NavItem to="/admin/data-import" icon={<UploadIcon size={20} data-id="element-225" />} label="Importar datos" onClick={onCloseMobileMenu} data-id="element-224" />
              <div className="border-t border-gray-200 my-2" data-id="element-226"></div>
              <div className="px-3 py-2" data-id="element-227">
                <p className="text-xs font-semibold text-gray-500 uppercase" data-id="element-228">
                  Vista Financiera
                </p>
              </div>
            </>}
          <NavItem to="/" icon={<HomeIcon size={20} data-id="element-230" />} label="Dashboard" onClick={onCloseMobileMenu} data-id="element-229" />
          <NavItem to="/income-statement" icon={<BarChartIcon size={20} data-id="element-232" />} label="Estado de Resultados" onClick={onCloseMobileMenu} data-id="element-231" />
          <NavItem to="/cash-flow" icon={<LineChartIcon size={20} data-id="element-234" />} label="Flujo de Efectivo" onClick={onCloseMobileMenu} data-id="element-233" />
          <NavItem to="/balance-sheet" icon={<PieChartIcon size={20} data-id="element-236" />} label="Balance General" onClick={onCloseMobileMenu} data-id="element-235" />
          <NavItem to="/trends" icon={<TrendingUpIcon size={20} data-id="element-238" />} label="Tendencias" onClick={onCloseMobileMenu} data-id="element-237" />
          <NavItem to="/settings" icon={<SettingsIcon size={20} data-id="element-240" />} label="Configuración" onClick={onCloseMobileMenu} data-id="element-239" />
        </nav>
      </motion.aside>
    </>;
};
interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
}
const NavItem: React.FC<NavItemProps> = ({
  to,
  icon,
  label,
  onClick
}) => {
  return <NavLink to={to} onClick={onClick} className={({
    isActive
  }) => `flex items-center space-x-3 px-3 py-2 rounded-md transition-colors ${isActive ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-241">
      <span data-id="element-242">{icon}</span>
      <span data-id="element-243">{label}</span>
    </NavLink>;
};
export default Sidebar;